// src/lib/pushNotif.js
// Helper para suscripción de Web Push notifications (PWA).
//
// Flujo:
// 1. Usuario click "Activar alertas" → solicitarPermiso()
// 2. Pide permiso al browser (Notification.requestPermission)
// 3. Suscribe al pushManager con la VAPID public key
// 4. Manda la suscripción al backend para guardarla en push_subscriptions
//
// Para desactivar: desuscribir() limpia BD + browser.

import { supabase } from './supabase';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSoportado() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

export async function getEstadoActual() {
  if (!pushSoportado()) return { soportado: false };
  const permiso = Notification.permission; // 'default' | 'granted' | 'denied'
  let suscrito = false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    suscrito = !!sub;
  } catch (_) {}
  return { soportado: true, permiso, suscrito };
}

export async function solicitarPermiso() {
  if (!pushSoportado()) throw new Error('Tu navegador no soporta notificaciones push');
  if (!VAPID_PUBLIC_KEY) throw new Error('VAPID_PUBLIC_KEY no configurada');

  const permiso = await Notification.requestPermission();
  if (permiso !== 'granted') {
    throw new Error(permiso === 'denied'
      ? 'Las notificaciones están bloqueadas. Habilitalas en la configuración del navegador.'
      : 'Permiso no otorgado.');
  }

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }

  // Persistir en BD. RLS exige que user_id = auth.uid().
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Sesión inválida');

  const json = sub.toJSON();
  const { error } = await supabase
    .from('push_subscriptions')
    .upsert({
      user_id:    user.id,
      endpoint:   sub.endpoint,
      p256dh_key: json.keys?.p256dh,
      auth_key:   json.keys?.auth,
      user_agent: navigator.userAgent.slice(0, 200),
      activo:     true,
      ultima_usada: new Date().toISOString(),
    }, { onConflict: 'endpoint' });
  if (error) throw error;

  return { ok: true };
}

export async function desuscribir() {
  if (!pushSoportado()) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await supabase.from('push_subscriptions')
        .update({ activo: false })
        .eq('endpoint', sub.endpoint);
      await sub.unsubscribe();
    }
  } catch (e) {
    console.warn('[pushNotif] desuscribir', e?.message);
  }
}
